import dayjs from 'dayjs';

/**
 * Check if a value is null, empty string, or zero
 */
export const isNullEmptyOrZero = (value: any): boolean => {
  return value === null || value === '' || value === 0;
};

/**
 * Validate that weights sum to 100%
 */
export const validateWeightsSum = (weights: (string | number)[]): boolean => {
  const sum = weights.reduce((total, val) => {
    const numVal = typeof val === 'string' ? parseFloat(val) || 0 : val;
    return Number(total) + numVal;
  }, 0);
  return Math.abs(Number(sum) - 100) < 0.01; // Allow small floating point differences
};

/**
 * Get weight sum error message if invalid
 */
export const getWeightsErrorMessage = (weights: (string | number)[]): string => {
  const sum = weights.reduce((total, val) => {
    const numVal = typeof val === 'string' ? parseFloat(val) || 0 : val;
    return Number(total) + numVal;
  }, 0);
  return Math.abs(Number(sum) - 100) >= 0.01 ? 'Total weight must equal 100' : '';
};

/**
 * Get unit affix for display
 */
export const getUnitAffix = (unit: string | undefined): string => {
  if (unit === 'percent') return '(%)';
  if (unit === 'currency') return '($)';
  return '';
};

/**
 * Validate number is within range
 */
export const validateNumberInRange = (
  value: number | null,
  min: number,
  max?: number
): { valid: boolean; message?: string } => {
  if (value === null || isNaN(value)) {
    return { valid: false, message: 'Value is required' };
  }

  if (value < min) {
    return { valid: false, message: `Value must be at least ${min}` };
  }

  if (max !== undefined && value > max) {
    return { valid: false, message: `Value must not exceed ${max}` };
  }

  return { valid: true };
};

/**
 * Check if a parameter is a filter by data source
 */
export const isFilterByDataSource = (param: any): boolean => {
  return param && typeof param.dataSource === 'string';
};

/**
 * Format date for API based on range type
 */
export const formatDateForRange = (date: Date | null, rangeType: string, isStart: boolean = true): string => {
  if (!date) return '';

  if (rangeType === 'MY') {
    return isStart
      ? dayjs(date).startOf('month').format('YYYY-MM-DD')
      : dayjs(date).endOf('month').format('YYYY-MM-DD');
  }

  return dayjs(date).format('YYYY-MM-DD');
};

/**
 * Create debounced update function
 */
export const createDebouncedUpdate = <T>(updateFn: (value: T) => void, delay: number = 500) => {
  let timeoutId: NodeJS.Timeout;

  return (value: T) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      updateFn(value);
    }, delay);
  };
};

/**
 * Validate date range
 */
export const validateDateRange = (
  from: Date | null,
  to: Date | null,
  options: {
    requireFrom?: boolean;
    requireTo?: boolean;
    fromBeforeTo?: boolean;
  } = {}
): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (options.requireFrom && !from) {
    errors.push('From date is required');
  }

  if (options.requireTo && !to) {
    errors.push('To date is required');
  }

  if (options.fromBeforeTo && from && to && from >= to) {
    errors.push('From date must be before To date');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Parse results value
 */
export const parseResultsValue = (value: string | number): number => {
  if (typeof value === 'string' && value.trim() === '') return -1;

  const num = Number(value);
  return num > 0 ? num : -1;
};

/**
 * Get results display choice
 */
export const getResultsChoice = (value: number): 'all' | 'specify' => {
  return value === -1 || value === 0 ? 'all' : 'specify';
};
